import { Component, computed, OnInit, signal } from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { Router } from "@angular/router";
import { fadeAnimation } from "src/app/animations/fade-animation";
import { FormFieldErrorComponent } from "src/app/components/form-field-error/form-field-error.component";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { FileToUpload } from "src/app/models/FileToUpload";
import { TechCoverSoftwareRelease } from "src/app/models/TechCoverSoftwareRelease";
import { TechCoverTicketPriorityOptions } from "src/app/models/TechCoverTicketPriority";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { TechCoverTicketsDataService } from "src/app/services/tech-cover-tickets-data.service";
import { commonModulesImports, materialCommonImports } from "src/app/utils/import.utils";

@Component({
  selector: "app-tech-cover-create-ticket-form",
  templateUrl: "./tech-cover-create-ticket-form.component.html",
  styleUrls: ["./tech-cover-create-ticket-form.component.scss"],
  imports: [...materialCommonImports, ...commonModulesImports, FormFieldErrorComponent],
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "outline" } }],
  standalone: true,
  animations: [fadeAnimation],
})
export class TechCoverCreateTicketFormComponent implements OnInit {
  ticketForm = this.fb.group({
    issueTitle: new FormControl<string>("", [Validators.required, Validators.maxLength(255)]),
    issueDescription: new FormControl<string>("", [
      Validators.required,
      Validators.maxLength(32000),
    ]),
    productWithIssue: new FormControl<string>("", [Validators.required]),
    productRelease: new FormControl<string>("", [Validators.required]),
    localPriority: new FormControl("", [Validators.required]),
  });

  selectedFile: FileToUpload | null = null;
  localizationConstants = LocalizationConstants;
  isSuccessMessageVisible = false;
  ticketNumber?: string;
  userEmail = "";
  selectedProduct = signal<TechCoverSoftwareRelease | null>(null);

  businessImpactOptions = signal([]);
  priorityOptions = TechCoverTicketPriorityOptions;
  productOptions = computed(() => {
    return (
      this.techCoverTicketsDataService.supportedSoftware()?.map((product) => {
        return {
          id: product.softwareId,
          name: product.softwareName,
        };
      }) ?? []
    );
  });
  productVersionOptions = computed(
    () =>
      this.selectedProduct()?.softwareVersions.map((p) => ({
        id: p.softwareVersionId,
        name: p.softwareVersionId,
      })) ?? [],
  );

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private overlayService: GlobalOverlayService,
    private companyService: CompanyService,
    private techCoverTicketsDataService: TechCoverTicketsDataService,
  ) {}

  ngOnInit(): void {
    this.ticketForm.controls.productWithIssue.valueChanges.subscribe((newValue: string) => {
      this.handleProductChange(newValue);
    });
  }

  private handleProductChange(productId: string): void {
    // Reset product version when product changes
    this.ticketForm.controls.productRelease.reset();

    this.selectedProduct.set(
      this.techCoverTicketsDataService.supportedSoftware().find((p) => p.softwareId === productId),
    );
  }

  onFileChange(file: FileToUpload): void {
    this.selectedFile = file;
  }

  async onSubmitAsync(): Promise<void> {
    this.ticketForm.markAllAsTouched();
    if (!this.ticketForm.valid) return;

    this.overlayService.show();
    try {
      this.ticketNumber = await this.techCoverTicketsDataService.createTicketAsync({
        companyId: this.companyService.selectedCompany.name,
        title: this.ticketForm.value.issueTitle,
        description: this.ticketForm.value.issueDescription,
        softwareId: this.ticketForm.value.productWithIssue,
        softwareVersionId: this.ticketForm.value.productRelease,
        priority: this.ticketForm.value.localPriority,
      });

      this.showSuccessMessage();
    } finally {
      this.overlayService.close();
    }
  }

  onCancel(): void {
    this.ticketForm.reset();
    this.navigateToTechCover();
  }

  private showSuccessMessage(): void {
    this.isSuccessMessageVisible = true;
  }

  onSuccessMessageClose() {
    this.isSuccessMessageVisible = false;
    this.navigateToTechCover();
  }

  navigateToTechCover(): void {
    this.router.navigate([RouteNameConstants.TECH_COVER]);
  }
}
